/**
 * Email Template Service
 * Manages email templates for invoices and notifications
 */

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  getDocs,
  getDoc,
  serverTimestamp,
  onSnapshot,
} from 'firebase/firestore';
import { db } from './firebase';

/**
 * Create a new email template
 */
export const createEmailTemplate = async (userId, templateData) => {
  try {
    const template = {
      userId,
      name: templateData.name,
      subject: templateData.subject,
      body: templateData.body, // HTML content
      type: templateData.type || 'invoice', // 'invoice', 'reminder', 'receipt'
      isDefault: templateData.isDefault || false,
      
      // Template variables available:
      // {{clientName}}, {{invoiceNumber}}, {{amount}}, {{dueDate}}, 
      // {{companyName}}, {{paymentLink}}, {{lineItems}}, etc.
      
      active: true,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    const docRef = await addDoc(collection(db, 'emailTemplates'), template);
    return { templateId: docRef.id, ...template };
  } catch (error) {
    console.error('Error creating email template:', error);
    throw error;
  }
};

/**
 * Update an email template
 */
export const updateEmailTemplate = async (templateId, templateData) => {
  try {
    const templateRef = doc(db, 'emailTemplates', templateId);
    await updateDoc(templateRef, {
      ...templateData,
      updatedAt: serverTimestamp(),
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating email template:', error);
    throw error;
  }
};

/**
 * Delete an email template
 */
export const deleteEmailTemplate = async (templateId) => {
  try {
    const templateRef = doc(db, 'emailTemplates', templateId);
    await deleteDoc(templateRef);
    return { success: true };
  } catch (error) {
    console.error('Error deleting email template:', error);
    throw error;
  }
};

/**
 * Get a single email template
 */
export const getEmailTemplate = async (templateId) => {
  try {
    const templateRef = doc(db, 'emailTemplates', templateId);
    const templateDoc = await getDoc(templateRef);

    if (!templateDoc.exists()) {
      throw new Error('Email template not found');
    }

    return { id: templateDoc.id, ...templateDoc.data() };
  } catch (error) {
    console.error('Error getting email template:', error);
    throw error;
  }
};

/**
 * Get all email templates for a user
 */
export const getEmailTemplates = async (userId, filters = {}) => {
  try {
    let q = query(
      collection(db, 'emailTemplates'),
      where('userId', '==', userId),
      orderBy('name', 'asc')
    );

    if (filters.type) {
      q = query(q, where('type', '==', filters.type));
    }

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error getting email templates:', error);
    throw error;
  }
};

/**
 * Get default template for a type
 */
export const getDefaultTemplate = async (userId, type = 'invoice') => {
  try {
    const q = query(
      collection(db, 'emailTemplates'),
      where('userId', '==', userId),
      where('type', '==', type),
      where('isDefault', '==', true)
    );

    const snapshot = await getDocs(q);
    if (snapshot.empty) {
      return null;
    }

    const doc = snapshot.docs[0];
    return { id: doc.id, ...doc.data() };
  } catch (error) {
    console.error('Error getting default template:', error);
    throw error;
  }
};

/**
 * Subscribe to email templates
 */
export const subscribeToEmailTemplates = (userId, callback) => {
  const q = query(
    collection(db, 'emailTemplates'),
    where('userId', '==', userId),
    orderBy('name', 'asc')
  );

  return onSnapshot(q, (snapshot) => {
    const templates = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    callback(templates);
  });
};

/**
 * Default email templates
 */
export const DEFAULT_TEMPLATES = {
  invoice: {
    name: 'Default Invoice Email',
    subject: 'Invoice {{invoiceNumber}} from {{companyName}}',
    body: `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h2>Invoice {{invoiceNumber}}</h2>
        <p>Dear {{clientName}},</p>
        <p>Thank you for your business. Please find your invoice details below:</p>
        
        <div style="background: #f5f5f5; padding: 20px; margin: 20px 0; border-radius: 5px;">
          <p><strong>Invoice Number:</strong> {{invoiceNumber}}</p>
          <p><strong>Issue Date:</strong> {{issueDate}}</p>
          <p><strong>Due Date:</strong> {{dueDate}}</p>
          <p><strong>Amount Due:</strong> {{currency}} {{amount}}</p>
        </div>
        
        {{#if lineItems}}
        <h3>Line Items:</h3>
        <table style="width: 100%; border-collapse: collapse;">
          <thead>
            <tr style="background: #f0f0f0;">
              <th style="padding: 10px; text-align: left; border: 1px solid #ddd;">Description</th>
              <th style="padding: 10px; text-align: right; border: 1px solid #ddd;">Amount</th>
            </tr>
          </thead>
          <tbody>
            {{#each lineItems}}
            <tr>
              <td style="padding: 10px; border: 1px solid #ddd;">{{description}}</td>
              <td style="padding: 10px; text-align: right; border: 1px solid #ddd;">{{amount}}</td>
            </tr>
            {{/each}}
          </tbody>
        </table>
        {{/if}}
        
        {{#if paymentLink}}
        <div style="margin: 30px 0; text-align: center;">
          <a href="{{paymentLink}}" style="background: #007bff; color: white; padding: 12px 30px; text-decoration: none; border-radius: 5px; display: inline-block;">
            Pay Now
          </a>
        </div>
        {{/if}}
        
        {{#if paymentInstructions}}
        <div style="margin: 20px 0;">
          <h3>Payment Instructions:</h3>
          <p>{{{paymentInstructions}}}</p>
        </div>
        {{/if}}
        
        {{#if notes}}
        <div style="margin: 20px 0;">
          <p><strong>Notes:</strong></p>
          <p>{{notes}}</p>
        </div>
        {{/if}}
        
        <p style="margin-top: 30px;">Best regards,<br>{{companyName}}</p>
        
        <div style="margin-top: 40px; padding-top: 20px; border-top: 1px solid #ddd; font-size: 12px; color: #666;">
          <p>This is an automated email. Please do not reply directly to this email.</p>
        </div>
      </div>
    `,
    type: 'invoice',
    isDefault: true,
  },
  reminder: {
    name: 'Default Payment Reminder',
    subject: 'Payment Reminder: Invoice {{invoiceNumber}}',
    body: `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h2>Payment Reminder</h2>
        <p>Dear {{clientName}},</p>
        <p>This is a friendly reminder that payment for invoice {{invoiceNumber}} is due.</p>
        
        <div style="background: #fff3cd; padding: 20px; margin: 20px 0; border-radius: 5px; border-left: 4px solid #ffc107;">
          <p><strong>Invoice Number:</strong> {{invoiceNumber}}</p>
          <p><strong>Due Date:</strong> {{dueDate}}</p>
          <p><strong>Amount Due:</strong> {{currency}} {{amount}}</p>
        </div>
        
        {{#if paymentLink}}
        <div style="margin: 30px 0; text-align: center;">
          <a href="{{paymentLink}}" style="background: #28a745; color: white; padding: 12px 30px; text-decoration: none; border-radius: 5px; display: inline-block;">
            Pay Now
          </a>
        </div>
        {{/if}}
        
        <p>If you have already made the payment, please disregard this reminder.</p>
        <p>If you have any questions, please don't hesitate to contact us.</p>
        
        <p style="margin-top: 30px;">Best regards,<br>{{companyName}}</p>
      </div>
    `,
    type: 'reminder',
    isDefault: true,
  },
};
